import { call, fork, take, put } from 'redux-saga/effects'
import { bindAsyncAction } from 'typescript-fsa-redux-saga'
import { Action } from 'typescript-fsa'
import {
  AboSettingsApi,
  AutorisationROApi,
  SmPublishConnectApi,
  SubsidiaryRESTServiceApi,
  TmpAdminApi,
  UserHasPageApi,
  UserLoginRESTServiceApi
} from '../../api/instances'
import {
  ForceDisconnectRequest,
  InitiateConnectOperationRequest
} from '@professocialApi/professocial-core-frontend-api/apis/SmPublishConnectApi'
import i18n from 'i18next'
import {
  createUserAccount,
  createUserAccountInitiated,
  deleteUserAccount,
  deleteUserAccountInitiated,
  forceDisconnect, forceDisconnectInitiated,
  getAboSettingsForPageIdentifiers,
  getAboSettingsForPageIdentifiersInitiated,
  getAllSubsidiaries,
  getAllSubsidiariesInitiated,
  getAllUsers,
  getAllUsersInitiated,
  getPageIdentifiersForUser,
  getPageIdentifiersForUserInitiated, getTokenForPage, getTokenForPageInitiated,
  getUserAccount,
  getUserAccountInitiated,
  initiateFacebookConnect,
  initiateFacebookConnectInitiated,
  listSubsidiaries,
  listSubsidiariesInitiated,
  loginAs,
  loginAsInitiated,
  unmountFacebookPageByUser,
  unmountFacebookPageByUserInitiated,
  updateUserAccount,
  updateUserAccountInitiated,
} from './accounts.actions'
import {
  CreateUserAccountRequest,
  DeleteUserAccountRequest,
  GetAllUsersRequest,
  GetUserAccountRequest,
  UnmountFacebookPageByUserRequest,
  UpdateUserAccountRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/UserLoginRESTServiceApi'
import { ListSubsidiariesRequest } from '@professocialApi/professocial-core-frontend-api/apis/SubsidiaryRESTServiceApi'
import { LoginAsRequest } from '@professocialApi/professocial-core-frontend-api/apis/AutorisationROApi'
import { requestFailed, requestFinished, requestStarted } from '../common/common.actions'
import { EventType, Severity } from '@professocial/professocialTypes/types/messageTypes'
import { GetAboSettingsForPageIdentifiersRequest } from '@3m5/sm_publish-frontend-api/apis/AboSettingsApi'
import { GetPageIdentifiersForUserRequest } from '@professocialApi/professocial-core-frontend-api/apis/UserHasPageApi'
import { GetTokenForPageRequest } from '@professocialApi/professocial-core-frontend-api/apis/TmpAdminApi'

// createUserAccount
const createUserAccountWorker = bindAsyncAction(createUserAccount)(
  function* (params: CreateUserAccountRequest): any {
    return yield call([UserLoginRESTServiceApi, UserLoginRESTServiceApi.createUserAccount], params)
  },
)

function* watchCreateUserAccountInitiated() {
  while (true) {
    const action: Action<CreateUserAccountRequest> = yield take(createUserAccountInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield createUserAccountWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('accountsCommon:newUserCreated'),
          eventType: EventType.create,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('coreMessages:createError', { context: i18n.t('accountsCommon:user') }),
          eventType: EventType.create,
        },
      }))
      console.error('error in watchCreateUserAccountInitiated: ', e)
    }
  }
}

// deleteUserAccount
const deleteUserAccountWorker = bindAsyncAction(deleteUserAccount)(
  function* (params: DeleteUserAccountRequest): any {
    return yield call([UserLoginRESTServiceApi, UserLoginRESTServiceApi.deleteUserAccount], params)
  },
)

function* watchDeleteUserAccountInitiated() {
  while (true) {
    const action: Action<DeleteUserAccountRequest> = yield take(deleteUserAccountInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield deleteUserAccountWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('accountsCommon:userDeleted'),
          eventType: EventType.delete,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('coreMessages:deleteError', { context: i18n.t('accountsCommon:user') }),
          eventType: EventType.delete,
        },
      }))
      console.error('error in watchDeleteUserAccountInitiated: ', e)
    }
  }
}

// updateUserAccount
const updateUserAccountWorker = bindAsyncAction(updateUserAccount)(
  function* (params: UpdateUserAccountRequest): any {
    return yield call([UserLoginRESTServiceApi, UserLoginRESTServiceApi.updateUserAccount], params)
  },
)

function* watchUpdateUserAccountInitiated() {
  while (true) {
    const action: Action<UpdateUserAccountRequest> = yield take(updateUserAccountInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield updateUserAccountWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('accountsCommon:userChanged'),
          eventType: EventType.update,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('coreMessages:updateError', { context: i18n.t('accountsCommon:user') }),
          eventType: EventType.update,
        },
      }))
      console.error('error in watchUpdateUserAccountInitiated: ', e)
    }
  }
}

// getUserAccount
const getUserAccountWorker = bindAsyncAction(getUserAccount)(
  function* (params: GetUserAccountRequest): any {
    return yield call([UserLoginRESTServiceApi, UserLoginRESTServiceApi.getUserAccount], params)
  },
)

function* watchGetUserAccountInitiated() {
  while (true) {
    const action: Action<GetUserAccountRequest> = yield take(getUserAccountInitiated)
    try {
      yield getUserAccountWorker(action.payload)
    } catch (e) {
      console.error('error in watchGetUserAccountInitiated: ', e)
    }
  }
}

// getAllUsers
const getAllUsersWorker = bindAsyncAction(getAllUsers)(
  function* (params: GetAllUsersRequest): any {
    return yield call([UserLoginRESTServiceApi, UserLoginRESTServiceApi.getAllUsers], params)
  },
)

function* watchGetAllUsersInitiated() {
  while (true) {
    const action: Action<GetAllUsersRequest> = yield take(getAllUsersInitiated)
    try {
      yield getAllUsersWorker(action.payload)
    } catch (e) {
      console.error('error in watchGetAllUsersInitiated: ', e)
    }
  }
}

// getAllSubsidiaries
const getAllSubsidiariesWorker = bindAsyncAction(getAllSubsidiaries)(
  function* (): any {
    return yield call([UserLoginRESTServiceApi, UserLoginRESTServiceApi.getAllSubsidiaries])
  },
)

function* watchGetAllSubsidiariesInitiated() {
  while (true) {
    const action: Action<undefined> = yield take(getAllSubsidiariesInitiated)
    try {
      yield getAllSubsidiariesWorker(action.payload)
    } catch (e) {
      console.error('error in watchGetAllSubsidiariesInitiated: ', e)
    }
  }
}

// listSubsidiaries
const listSubsidiariesWorker = bindAsyncAction(listSubsidiaries)(
  function* (params: ListSubsidiariesRequest): any {
    return yield call([SubsidiaryRESTServiceApi, SubsidiaryRESTServiceApi.listSubsidiaries], params)
  },
)

function* watchListSubsidiariesInitiated() {
  while (true) {
    const action: Action<ListSubsidiariesRequest> = yield take(listSubsidiariesInitiated)
    try {
      yield listSubsidiariesWorker(action.payload)
    } catch (e) {
      console.error('error in watchListSubsidiariesInitiated: ', e)
    }
  }
}

const unmountFacebookPageByUserWorker = bindAsyncAction(unmountFacebookPageByUser)(
  function* (params: UnmountFacebookPageByUserRequest): any {
    return yield call([UserLoginRESTServiceApi, UserLoginRESTServiceApi.unmountFacebookPageByUser], params)
  },
)

function* watchUnmountFacebookPageByUserInitiated() {
  while (true) {
    const action: Action<UnmountFacebookPageByUserRequest> = yield take(unmountFacebookPageByUserInitiated)
    try {
      yield unmountFacebookPageByUserWorker(action.payload)
    } catch (e) {
      console.error('error in watchUnmountFacebookPageByUserInitiated: ', e)
    }
  }
}

// loginAs
const loginAsWorker = bindAsyncAction(loginAs)(
  function* (params: LoginAsRequest): any {
    return yield call([AutorisationROApi, AutorisationROApi.loginAs], params)
  },
)

function* watchLoginAsInitiated() {
  while (true) {
    const action: Action<LoginAsRequest> = yield take(loginAsInitiated)
    try {
      yield loginAsWorker(action.payload)
    } catch (e) {
      console.error('error in watchLoginAsInitiated: ', e)
    }
  }
}

// getAboSettingsForCurrentUser
const getAboSettingsForPageIdentifiersWorker = bindAsyncAction(getAboSettingsForPageIdentifiers)(
  function* (params: GetAboSettingsForPageIdentifiersRequest): any {
    return yield call([AboSettingsApi, AboSettingsApi.getAboSettingsForPageIdentifiers], params)
  },
)

function* watchGetAboSettingsForPageIdentifiersInitiated() {
  while (true) {
    const action: Action<GetAboSettingsForPageIdentifiersRequest> = yield take(getAboSettingsForPageIdentifiersInitiated)
    try {
      yield getAboSettingsForPageIdentifiersWorker(action.payload)
    } catch (e) {
      console.error('error in watchGetAboSettingsForCurrentUserInitiated: ', e)
    }
  }
}

// forceDisconnect
const forceDisconnectWorker = bindAsyncAction(forceDisconnect)(
  function* (params: ForceDisconnectRequest): any {
    return yield call([SmPublishConnectApi, SmPublishConnectApi.forceDisconnect], params)
  },
)

function* watchForceDisconnectInitiated() {
  while (true) {
    const action: Action<ForceDisconnectRequest> = yield take(forceDisconnectInitiated)
    yield put(requestStarted({ type: action.type, id: action.payload.userHasPage.networkPageId }))
    try {
      yield forceDisconnectWorker(action.payload)
      yield put(requestFinished({ type: action.type, id: action.payload.userHasPage.networkPageId }))
    } catch (e) {
      yield put(requestFailed({ type: action.type, id: action.payload.userHasPage.networkPageId }))
      console.error('error in watchForceDisconnectInitiated: ', e)
    }
  }
}

// initiateFacebookConnect
const initiateFacebookConnectWorker = bindAsyncAction(initiateFacebookConnect)(
  function* (params: InitiateConnectOperationRequest): any {
    return yield call([SmPublishConnectApi, SmPublishConnectApi.initiateConnect], params)
  },
)

function* watchInitiateFacebookConnectInitiated() {
  while (true) {
    const action: Action<InitiateConnectOperationRequest> = yield take(initiateFacebookConnectInitiated)
    try {
      yield initiateFacebookConnectWorker(action.payload)
    } catch (e) {
      console.error('error in watchInitiateFacebookConnectInitiated: ', e)
    }
  }
}

// getPageIdentifiersForUser
const getPageIdentifiersForUserWorker = bindAsyncAction(getPageIdentifiersForUser)(
  function* (): any {
    return yield call({ context: UserHasPageApi, fn: UserHasPageApi.getPageIdentifiersForCurrentUser })
  },
)

function* watchGetPageIdentifiersForUserInitiated(): any {
  while (true) {
    const action: Action<GetPageIdentifiersForUserRequest> = yield take(getPageIdentifiersForUserInitiated)
    try {
      yield getPageIdentifiersForUserWorker()
    } catch (e) {
      console.error('error in watchGetPageIdentifiersForUserInitiated: ', e)
    }
  }
}

// getTokenForPage
const getTokenForPageWorker = bindAsyncAction(getTokenForPage)(
  function* (params: GetTokenForPageRequest): any {
    return yield call([TmpAdminApi, TmpAdminApi.getTokenForPage], params)
  },
)

function* watchGetTokenForPageInitiated() {
  while (true) {
    const action: Action<GetTokenForPageRequest> = yield take(getTokenForPageInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield getTokenForPageWorker(action.payload)
      yield put(requestFinished({ type: action.type }))
    } catch (e) {
      yield put(requestFailed({ type: action.type }))
      console.error('error in watchGetTokenForPageInitiated: ', e)
    }
  }
}

export function* accountsSagas() {
  yield fork(watchCreateUserAccountInitiated)
  yield fork(watchDeleteUserAccountInitiated)
  yield fork(watchUpdateUserAccountInitiated)
  yield fork(watchGetAllUsersInitiated)
  yield fork(watchUnmountFacebookPageByUserInitiated)
  yield fork(watchGetAllSubsidiariesInitiated)
  yield fork(watchListSubsidiariesInitiated)
  yield fork(watchGetUserAccountInitiated)
  yield fork(watchLoginAsInitiated)
  yield fork(watchGetAboSettingsForPageIdentifiersInitiated)
  yield fork(watchForceDisconnectInitiated)
  yield fork(watchInitiateFacebookConnectInitiated)
  yield fork(watchGetPageIdentifiersForUserInitiated)
  yield fork(watchGetTokenForPageInitiated)
}
